export type NetworkFeeType = 'none' | 'percent' | 'fixed';

export type NetworkFeeConfig = {
  type?: NetworkFeeType;
  rate?: number;
  fixedUsd?: number;
};

export function computeNetWithdrawalFeePreview(
  netUsd: number,
  systemFeeRate: number,
  gatewayFeeRate: number,
  networkFee?: NetworkFeeConfig,
  gatewayFeeMinUsd = 0,
) {
  const net = Math.round(netUsd * 100) / 100;
  const systemRate = Number.isFinite(systemFeeRate) && systemFeeRate >= 0 ? systemFeeRate : 0;
  const gatewayRate = Number.isFinite(gatewayFeeRate) && gatewayFeeRate >= 0 ? gatewayFeeRate : 0;
  const minUsd = Number.isFinite(gatewayFeeMinUsd) && gatewayFeeMinUsd >= 0 ? gatewayFeeMinUsd : 0;
  const systemFee = Math.round(net * systemRate * 100) / 100;
  const gatewayPercentFee = Math.round(net * gatewayRate * 100) / 100;
  const gatewayFee =
    minUsd > 0
      ? Math.round(Math.max(gatewayPercentFee, minUsd) * 100) / 100
      : gatewayPercentFee;
  const type = String(networkFee?.type || 'none').toLowerCase();
  let networkFeeAmount = 0;
  if (type === 'percent') {
    const rate = Number(networkFee?.rate ?? 0);
    const r = Number.isFinite(rate) && rate >= 0 ? rate : 0;
    networkFeeAmount = Math.round(net * r * 100) / 100;
  } else if (type === 'fixed') {
    const fixed = Number(networkFee?.fixedUsd ?? 0);
    networkFeeAmount = Math.round((Number.isFinite(fixed) && fixed >= 0 ? fixed : 0) * 100) / 100;
  }
  const fee = Math.round((systemFee + gatewayFee + networkFeeAmount) * 100) / 100;
  return {
    net,
    systemFee,
    gatewayFee,
    networkFee: networkFeeAmount,
    fee,
    tierFunding: Math.round((net + systemFee + gatewayFee + networkFeeAmount) * 100) / 100,
    gatewayPayout: Math.round((net + systemFee + gatewayFee + networkFeeAmount) * 100) / 100,
  };
}

export function formatDepositFeeSummary(params: {
  feeRate?: number | null;
  networkFeeType?: NetworkFeeType | string | null;
  networkFeeRate?: number | null;
  networkFeeAmount?: number | null;
}): string {
  const parts: string[] = [];
  const feeRate = Number(params.feeRate ?? 0);
  if (Number.isFinite(feeRate) && feeRate > 0) {
    parts.push(`${(feeRate * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}% platform`);
  }
  const type = String(params.networkFeeType || 'none').toLowerCase();
  if (type === 'percent') {
    const rate = Number(params.networkFeeRate ?? 0);
    if (Number.isFinite(rate) && rate > 0) {
      parts.push(`${(rate * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}% network`);
    }
  } else if (type === 'fixed') {
    const amount = Number(params.networkFeeAmount ?? 0);
    if (Number.isFinite(amount) && amount > 0) {
      parts.push(`$${amount.toFixed(2)} network`);
    }
  }
  return parts.length > 0 ? parts.join(' + ') : '—';
}

function formatPercentRate(rate: number): string {
  return `${(rate * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

export function formatDepositFeeBreakdown(params: {
  gatewayFeeRate?: number | null;
  systemFeeRate?: number | null;
  networkFeeType?: NetworkFeeType | string | null;
  networkFeeRate?: number | null;
  networkFeeAmount?: number | null;
}) {
  const gatewayFeeRate = Number(params.gatewayFeeRate ?? 0);
  const systemFeeRate = Number(params.systemFeeRate ?? 0);
  const type = String(params.networkFeeType || 'none').toLowerCase();
  const networkFeeRate = Number(params.networkFeeRate ?? 0);
  const networkFeeAmount = Number(params.networkFeeAmount ?? 0);

  const safeGatewayFeeRate = Number.isFinite(gatewayFeeRate) && gatewayFeeRate > 0 ? gatewayFeeRate : 0;
  const safeSystemFeeRate = Number.isFinite(systemFeeRate) && systemFeeRate > 0 ? systemFeeRate : 0;
  const safeNetworkFeeRate = type === 'percent' && Number.isFinite(networkFeeRate) && networkFeeRate > 0 ? networkFeeRate : 0;
  const safeNetworkFeeAmount = type === 'fixed' && Number.isFinite(networkFeeAmount) && networkFeeAmount > 0 ? networkFeeAmount : 0;
  const totalPercentRate = safeGatewayFeeRate + safeSystemFeeRate + safeNetworkFeeRate;

  const networkFee =
    safeNetworkFeeRate > 0
      ? formatPercentRate(safeNetworkFeeRate)
      : safeNetworkFeeAmount > 0
        ? `$${safeNetworkFeeAmount.toFixed(2)}`
        : '—';
  const totalParts = [
    totalPercentRate > 0 ? formatPercentRate(totalPercentRate) : null,
    safeNetworkFeeAmount > 0 ? `$${safeNetworkFeeAmount.toFixed(2)}` : null,
  ].filter(Boolean);

  return {
    gatewayProcessingFee: safeGatewayFeeRate > 0 ? formatPercentRate(safeGatewayFeeRate) : '—',
    networkFee,
    systemFee: safeSystemFeeRate > 0 ? formatPercentRate(safeSystemFeeRate) : '—',
    totalFee: totalParts.length > 0 ? totalParts.join(' + ') : '—',
  };
}
